const PRODUCT_SELECT_EVENT = 'shopify:product:select';

function serviceMode(value = '') {
  const normalized = value.toLowerCase();
  if (normalized.includes('mobile') || normalized.includes('home')) return 'mobile';
  if (normalized.includes('workshop') || normalized.includes('installation')) return 'workshop';
  return 'product_only';
}

class InstallationDetails extends HTMLElement {
  connectedCallback() {
    this.productScope = this.closest('.product-information, featured-product-information') || document;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onServiceChange = this.onServiceChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.productScope.addEventListener(PRODUCT_SELECT_EVENT, this.onVariantChange);
    this.addEventListener('change', this.onServiceChange);
    this.form = document.getElementById(this.dataset.formId);
    if (this.dataset.usesServiceProducts === 'true') this.form?.addEventListener('submit', this.onSubmit, true);
    this.setMinimumDate();
    this.update(this.dataset.mode || 'product_only');
  }

  disconnectedCallback() {
    this.productScope?.removeEventListener(PRODUCT_SELECT_EVENT, this.onVariantChange);
    this.removeEventListener('change', this.onServiceChange);
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  onVariantChange(event) {
    if (this.dataset.usesServiceProducts === 'true') return;
    const purchaseOption = event.selectedOptions?.find((option) => {
      const name = option.name.toLowerCase();
      return name.includes('purchase option') || name.includes('installation');
    });

    if (purchaseOption) this.update(serviceMode(purchaseOption.value));
  }

  onServiceChange(event) {
    if (!event.target.matches('[name="installation-service-choice"]')) return;
    this.selectedServiceVariantId = event.target.dataset.serviceVariantId || '';
    this.update(event.target.value);
  }

  async onSubmit(event) {
    if (this.dataset.mode === 'product_only' || !this.selectedServiceVariantId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.form?.reportValidity()) return;

    const variantId = this.form.querySelector('[name="id"]')?.value;
    const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
    if (!variantId) return;

    const properties = {};
    for (const [name, value] of new FormData(this.form)) {
      const match = name.match(/^properties\[(.+)]$/);
      if (match && value) properties[match[1]] = value;
    }

    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(variantId), quantity, properties },
            {
              id: Number(this.selectedServiceVariantId),
              quantity,
              properties: {
                _installation_for_product_id: this.dataset.productId,
                _installation_for_variant_id: variantId,
                'Installation for': this.dataset.productTitle
              }
            }
          ]
        })
      });
      if (!response.ok) throw new Error((await response.json()).description || 'Unable to add installation');
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      if (submitButton) submitButton.disabled = false;
      window.alert(error.message);
    }
  }

  setMinimumDate() {
    const dateInput = this.querySelector('[data-installation-date]');
    if (!dateInput) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    dateInput.min = `${year}-${month}-${day}`;
  }

  update(mode) {
    this.dataset.mode = mode;
    const installationSelected = mode !== 'product_only';
    const mobileSelected = mode === 'mobile';
    const service = this.querySelector('[data-installation-service]');

    if (service) {
      service.value = mobileSelected
        ? 'Mobile installation'
        : installationSelected
          ? 'Workshop installation'
          : 'Product only';
    }

    this.togglePanel('[data-installation-fields]', installationSelected);
    this.togglePanel('[data-workshop-details]', mode === 'workshop');
    this.togglePanel('[data-mobile-details]', mobileSelected);

    this.querySelectorAll('[data-installation-required]').forEach((field) => {
      field.required = installationSelected;
      field.disabled = !installationSelected;
    });
    this.querySelectorAll('[data-mobile-required]').forEach((field) => {
      field.required = mobileSelected;
      field.disabled = !mobileSelected;
    });

    // Express checkout can bypass required line-item properties, so installation
    // variants always use the normal Add to cart path.
    const acceleratedCheckout = this.closest('form')?.querySelector('.accelerated-checkout-block');
    if (acceleratedCheckout) acceleratedCheckout.hidden = installationSelected;
  }

  togglePanel(selector, visible) {
    const panel = this.querySelector(selector);
    if (panel) panel.hidden = !visible;
  }
}

if (!customElements.get('installation-details')) {
  customElements.define('installation-details', InstallationDetails);
}
